import { UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { FilesSearchResult } from 'types/files_search_result';
import { OffsetPaginated, OffsetPaginationParams } from 'types/pagination';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';

import { useFilesSearchClient } from './useFilesSearchClient';

export type FilesSearchEntity =
  | 'objects'
  | 'events'
  | 'texts'
  | 'trackables'
  | 'vectors';

export type ImageSearchType = 'person_reid' | 'face_reid';

export type UseFilesSearchParams = {
  query?: string;
  /** Leave undefined to apply search to all types */
  search_entities?: FilesSearchEntity[];
  /**
   * @min 0
   * @max 1
   * @step 0.1
   */
  confidence?: number;
  /** @default 'relevance' */
  sort_by?: 'relevance' | 'created_at';
  created_ts_since?: string;
  created_ts_until?: string;
  camera_ids?: string[];
  stream_ids?: string[];
  gateway_ids?: string[];
  file_ids?: string[];
  /** Returns one result per trackable with latest match */
  latest_trackable_hit?: boolean;
  /** Image search type — triggers POST with embedding */
  type?: ImageSearchType;
  /** Embedding vector for image search */
  embedding?: number[];
};

type UseFilesSearchOptions = UseQueryOptions<
  OffsetPaginated<FilesSearchResult>,
  APIError
> & {
  params?: UseFilesSearchParams & OffsetPaginationParams;
};

export function useFilesSearch({
  queryKey = ['files'],
  params,
  ...options
}: UseFilesSearchOptions = {}) {
  const client = useFilesSearchClient();
  const { applicationID } = useAPI();

  const { type, embedding, ...restParams } = params ?? {};

  const queryParams = {
    pagination: 'offset',
    page: 1,
    limit: 50,
    ...restParams,
  };

  const isImageSearch = Boolean(type && embedding?.length);

  return useAuthenticatedQuery(
    [...queryKey, applicationID, queryParams, type, embedding],
    async () => {
      const { data } = await client.post<OffsetPaginated<FilesSearchResult>>(
        `/apps/${applicationID}/search`,
        isImageSearch ? { type, embedding } : {},
        { params: queryParams }
      );

      return data;
    },
    options
  );
}
